import React, { useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { FiUpload, FiFile } from "react-icons/fi";

interface ImportApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<void>;
  loading: boolean;
}

export const ImportApplicationModal: React.FC<ImportApplicationModalProps> = ({
  isOpen,
  onClose,
  onImport,
  loading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const toast = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type if needed
      const allowedTypes = [".zip", ".json", ".xml"]; // Adjust based on your backend requirements
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (!allowedTypes.includes(fileExtension)) {
        toast({
          title: "Invalid File Type",
          description: `Please select a valid file type: ${allowedTypes.join(
            ", "
          )}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to import.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    await onImport(selectedFile);
  };

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import Application</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Box
              w="100%"
              h="200px"
              border="2px dashed"
              borderColor="gray.300"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              _hover={{ borderColor: "brand.500", bg: "gray.50" }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,.json,.xml"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <VStack>
                <Icon
                  as={selectedFile ? FiFile : (FiUpload as any)}
                  boxSize="40px"
                  color="gray.400"
                />
                <Text textAlign="center" color="gray.500">
                  {selectedFile ? (
                    <>
                      <Text fontWeight="semibold" color="brand.600">
                        {selectedFile.name}
                      </Text>
                      <Text fontSize="sm">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Text>
                    </>
                  ) : (
                    <>
                      Drag and drop your application file here, or{" "}
                      <Text as="span" color="brand.600" fontWeight="semibold">
                        browse
                      </Text>
                    </>
                  )}
                </Text>
              </VStack>
            </Box>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Supported format: ZIP
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleImport}
            isLoading={loading}
            loadingText="Importing..."
            isDisabled={!selectedFile}
          >
            Import
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
